import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RequiredRule, CustomRule } from 'devextreme-react/data-grid'

import { useQuery, useListQuery, useMutation } from 'components/graphql'
import { listProductsGridView, getDataGridImport } from './gql/query'
import { Select } from 'components/forms'
import listCategories from 'sections/category/gql/listCategories'
import { listSalesGroups, loadAppConstants } from 'gql/query'
import upsertProducts from './gql/mutation/upsertProducts'
import GridView from 'components/GridView'
import { useParams } from 'react-router-dom'
import numberFormatter from '../../helpers/numberFormatter'
import useLocale from 'hooks/useLocale'
import { convertNumber } from 'components/forms/Number/Field'
import getMWStName from 'helpers/getMWStName'

const MWStEnums = ['normal', 'reduced', 'free', 'special']

const GridViewProduct = props => {
	const locale = useLocale()
	const { id: recordId } = useParams()
	const [imported, setImported] = useState()
	const [selectedRows, setSelectedRows] = useState([])
	const [isMultiAddEnabled, setIsMultiAddEnabled] = useState(false)
	const [t, i18n] = useTranslation()
	const [handleUpsert] = useMutation(upsertProducts)
	const { data: tableData, loading } = useListQuery(listProductsGridView)
	const { data: saleUnits } = useListQuery(loadAppConstants, {
		variables: { type: 34, language: i18n.language }
	})
	const { data: mwstOptions } = useListQuery(loadAppConstants, {
		variables: { type: 33, language: i18n.language }
	})
	const { data: validateImportData, networkStatus } = useQuery(getDataGridImport, { limit: 300 })

	const allSalesGroupsImported = validateImportData?.listSalesGroups?.items || []

	const displayCellValue = cell => typeof cell?.value === 'string' ? cell?.valueText : cell.value?.name || ''

	const handleSaveRows = e => {
		let records = []
		if(!imported) {
			records = e.changes.map(({ type, data, key }) => {
				const updatedRecord = {
					...(type === 'update' && key),
					...type === 'insert' && { isActive: true },
					...data
				}
				const { __KEY__, id, __typename, category = {}, salesGroup = {}, ...fields } = updatedRecord
				return {
					id: key.id,
					fields: {
						...fields,
						categoryId: category?.id,
						salesGroupId: salesGroup?.id
					}
				}
			})
		} else {
			const allCategories = validateImportData?.listCategories?.items || []
			const allSalesGroups = validateImportData?.listSalesGroups?.items || []

			records = e?.changes?.reduce?.((outArr, { data }) => {
				if(data) {
					const fields = { isActive: true }
					Object.keys(data).forEach(key => {
						const cellVal = data[key]
						if(cellVal) {
							switch(key) {
								case 'category':
									fields.categoryId = typeof cellVal === 'string'
										? allCategories.find(item => item.name.toLowerCase() === cellVal.toLowerCase())?.id
										: cellVal?.id
									break
								case 'code':
									fields.code = typeof cellVal !== 'string' ? String(cellVal) : cellVal
									break
								case 'salesGroup':
									fields.salesGroupId = typeof cellVal === 'string'
										? allSalesGroups.find(item => item.name.toLowerCase() === cellVal.toLowerCase())?.id
										: cellVal?.id
									break
								case 'unitSellCode':
									fields.unitSellCode = isNaN(Number(cellVal))
										? saleUnits.find(item => item?.label?.toLowerCase?.() === cellVal?.toLowerCase?.())?.value
										: cellVal
									break
								case 'codeMWSt':
									let enumVal = MWStEnums.find(item => item === cellVal)
									if(!enumVal) {
										const enumInd = mwstOptions.find(opt => opt.label.toLowerCase() === cellVal.toLowerCase())?.value
										enumVal = MWStEnums[enumInd - 1]
									}
									fields.codeMWSt = enumVal
									break
								case 'price':
									fields.price = +data[key]
									break
								case 'priceInCafe':
									fields.priceInCafe = +data[key]
									break
								default: fields[key] = data[key]
							}
						}
					})
					outArr.push({ fields })
				}
				return outArr
			}, [])
		}
		e.promise = handleUpsert({
			variables: { records }
		})
	}
	const onSelectionChanged = (e) => {
		setSelectedRows(e.selectedRowsData.map(row => row.id))
	}

	const tableCols = [{
		caption: t('product_section.fields.number'),
		dataField: 'code',
		width: 100,
		children: () => (<RequiredRule message={t('validators.required')} />)
	}, {
		caption: t('product_section.fields.name'),
		dataField: 'name',
		children: () => (<RequiredRule message={t('validators.required')} />)
	}, /*{
		caption: t('product_section.fields.active'),
		dataField: 'isActive',
		width: 80
	}, */{
		caption: t('product_section.fields.category'),
		dataField: 'category',
		width: 200,
		customizeText: displayCellValue,
		editCellRender: cell => {
			return(
				<Select
					noForm
					isSearchable
					defaultMenuIsOpen
					defaultValue={cell.value}
					valueField='id'
					labelField='name'
					onChange={cell.setValue}
					withVirtualBody
					remoteOptions={{ query: listCategories, variables: { params: { limit: 50 } } }}
					menuPortalTarget={document.body}
					simpleValue={false}
					closeMenuOnSelect={!isMultiAddEnabled}
				/>
			)
		},
		...networkStatus === 7 && {
			children: () => (
				<CustomRule
					type='custom'
					ignoreEmptyValue={true}
					message={t('validators.invalid_value')}
					validationCallback={info => {
						if(typeof info?.value === 'string') { // Validate only imported data
							const allCategories = validateImportData?.listCategories?.items || []
							if(!allCategories.length) {
								return true
							}
							const val = info?.value?.trim?.()?.toLowerCase?.()
							return allCategories?.find?.(item => item?.name?.toLowerCase?.() === val)
						}
						return true
					}}
				/>
			)
		}
	}, {
		caption: t('product_section.fields.sales_group'),
		dataField: 'salesGroup',
		width: 220,
		customizeText: displayCellValue,
		editCellRender: cell => {
			return(
				<Select
					noForm
					isSearchable
					defaultMenuIsOpen
					defaultValue={cell.value}
					valueField='id'
					labelField='name'
					onChange={cell.setValue}
					remoteOptions={{ query: listSalesGroups }}
					simpleValue={false}
					menuPortalTarget={document.body}
					closeMenuOnSelect={!isMultiAddEnabled}
				/>
			)
		},
		...networkStatus === 7 && {
			children: () => (
				<CustomRule
					type='custom'
					ignoreEmptyValue
					message={t('validators.invalid_value')}
					validationCallback={({ value }) => {
						if(typeof value !== 'string') {
							return true
						}
						const allSalesGroups = validateImportData?.listSalesGroups?.items || []
						if(!allSalesGroups.length) {
							return true
						}
						return allSalesGroups?.find?.(item => item?.name?.toLowerCase?.() === value.toLowerCase())
					}}
				/>
			)
		}
	}, {
		caption: t('product_section.fields.sales_unit'),
		dataField: 'unitSellCode',
		width: 190,
		customizeText: cell => {
			if(cell?.value && !isNaN(Number(cell.value))) {
				return saleUnits.find(u => u.value === cell.value)?.label
			}
			return cell.valueText
		},
		editCellRender: cell => {
			return(
				<Select
					noForm
					isSearchable
					defaultMenuIsOpen
					defaultValue={saleUnits.find(u => u.value === cell.value)}
					onChange={opt => cell.setValue(opt.value)}
					simpleValue={false}
					options={saleUnits}
					menuPortalTarget={document.body}
					closeMenuOnSelect={!isMultiAddEnabled}
				/>
			)
		},
		...networkStatus === 7 && {
			children: () => (
				<CustomRule
					type='custom'
					ignoreEmptyValue={true}
					message={t('validators.invalid_value')}
					validationCallback={info => {
						const val = info?.value?.trim?.()?.toLowerCase?.()
						if(isNaN(Number(val))) {
							return saleUnits.find(opt => opt.label.toLowerCase() === val)
						}
						return true
					}}
				/>
			)
		}
	}, {
		caption: t('product_section.strings.price'),
		dataField: 'price',
		customizeText: cell => Array.isArray(cell?.value)
			? numberFormatter(cell?.value?.[0], { locale }) || ''
			: numberFormatter(cell?.valueText, { locale }) || '',
		alignment: 'right',
		width: 80
	}, {
		caption: t('product_section.strings.price_in_cafe'),
		dataField: 'priceInCafe',
		customizeText: cell => Array.isArray(cell?.value)
			? numberFormatter(cell?.value?.[0], { locale }) || ''
			: numberFormatter(cell?.valueText, { locale }) || '',
		alignment: 'right',
		width: 80
	}, {
		caption: t('product_section.fields.tax'),
		dataField: 'codeMWSt',
		customizeText: cell => {
			const getLabelByEnum = () => mwstOptions.find(u => MWStEnums[u.value - 1] === cell.value)?.label
			if(imported) {
				if(!cell?.valueText) {
					return ''
				}
				// Handle Select change after import
				const labelByEnum = getLabelByEnum()
				if(labelByEnum) {
					return labelByEnum
				}
				return cell?.valueText
			}
			return getLabelByEnum()
		},
		editCellRender: cell => {
			const options = mwstOptions.map(opt => ({
				value: MWStEnums[opt.value - 1],
				label: opt.label
			}))
			return(
				<Select
					noForm
					isSearchable
					defaultMenuIsOpen
					defaultValue={options.find(opt => opt.value === cell.value)}
					onChange={opt => cell.setValue(opt.value)}
					simpleValue={false}
					options={options}
					menuPortalTarget={document.body}
					closeMenuOnSelect={!isMultiAddEnabled}
				/>
			)
		},
		...networkStatus === 7 && {
			children: () => (
				<CustomRule
					type='custom'
					ignoreEmptyValue={true}
					message={t('validators.invalid_value')}
					validationCallback={info => {
						const val = info?.value?.trim?.()?.toLowerCase?.()
						return mwstOptions.find(opt => opt.label.toLowerCase() === val) || MWStEnums.indexOf(val) > -1
					}}
				/>
			)
		}
	}]

	const schema = [
		{
			column: t('product_section.fields.number'),
			value: row => row.code,
			width: 10
		},
		{
			column: t('product_section.fields.name'),
			value: row => row.name,
			width: 50
		},
		{
			column: t('product_section.fields.category'),
			value: row => row.category?.name,
			width: 25
		},
		{
			column: t('product_section.fields.sales_group'),
			value: row => allSalesGroupsImported?.find?.(item => item.id === row.salesGroup?.id)?.name,
			width: 25
		},
		{
			column: t('product_section.fields.sales_unit'),
			value: row => saleUnits?.find?.(item => item.value === row.unitSellCode)?.label,
			width: 15
		},
		{
			column: t('product_section.strings.price'),
			value: row => row.price ? (Number.isInteger(row.price[0]) ? row.price[0] : row.price[0]?.toFixed(2)) : '',
			width: 15,
			align: 'right'
		},
		{
			column: t('product_section.strings.price_in_cafe'),
			value: row => row.priceInCafe ? (Number.isInteger(row.priceInCafe[0]) ? row.priceInCafe[0] : row.priceInCafe[0]?.toFixed(2)) : '',
			width: 15,
			align: 'right'
		},
		{
			column: t('product_section.fields.tax'),
			value: row => getMWStName(row.codeMWSt, t),
			width: 20
		}
	]

	return (
		<GridView
			data={tableData}
			loading={loading}
			columns={tableCols}
			onSaving={handleSaveRows}
			onMultiEditEnabled={setIsMultiAddEnabled}
			selectedRows={selectedRows}
			onSelectionChanged={onSelectionChanged}
			exportData={{
				schema,
				sheetName: t('product_section.fields.product_list'),
				fileName: t('product_section.fields.product_list_underline')
			}}
			sectionPath={`/artikel${recordId ? '/' + recordId : ''}`}
			onEditorPreparing={e => {
				if(e.dataField === 'price' || e.dataField === 'priceInCafe') {
					const value = e.value
					e.editorOptions.value = Array.isArray(value) ? numberFormatter(value[0], { locale }) || '' : numberFormatter(value, { locale }) || ''
					e.editorOptions.onValueChanged = args => {
						if(Array.isArray(value)) {
							const newVal = [...value]
							newVal[0] = convertNumber(args.value, locale)
							e.setValue(newVal)
						} else {
							e.setValue(convertNumber(args.value, locale))
						}
					}
				}
			}}
			onImported={() => {
				if(!imported) {
					setImported(true)
				}
			}}
		/>
	)
}

export default GridViewProduct
